import { MEASURES, type MeasureDef, type MeasureId, type DimensionId } from "./metrics";

export type TimeGrain = "day" | "week";
export type PlotChartType =
  "xmr" | "xmr-log" | "x" | "p" | "ewma" | "line" | "boxplot" | "stacked-bar" | "distribution-xmr";
export type SubchartToken = "1D" | "2D" | DimensionId | [DimensionId, DimensionId];

export interface RatioComponents {
  numerator: MeasureId;
  denominator: MeasureId;
}

export interface EwmaConfig {
  lambda?: number;
  limitMultiplier?: number;
}

export interface BoxplotConfig {
  /** The dimension to aggregate over (e.g., "audience") */
  aggregateDimension: DimensionId;
  /** Lower whisker percentile (default: 0.10 for 10%) */
  lowerExtreme?: number;
  /** Upper whisker percentile (default: 0.90 for 90%) */
  upperExtreme?: number;
  /** Whether to use logarithmic scale for y-axis */
  logScale?: boolean;
}

export interface StackedBarConfig {
  /** Dimension to stack by (e.g., "audience", "organization") */
  dimension: DimensionId;
  /** Max categories before grouping to "Other" (default: 10) */
  maxCategories?: number;
}

export interface DistributionXmrConfig {
  /** Dimension to analyze (e.g., "audience", "organization") */
  dimension: DimensionId;
  /** Distribution assumption for statistics calculation */
  distribution: "normal" | "log-normal" | "logit-normal";
}

export interface SubchartsConfig {
  dims: SubchartToken[];
  sortByDesc?: MeasureId; // Sort/filter subcharts by this metric in descending order; if undefined, sorts by the metric itself
  limit?: number; // Number of top categories to keep
  collapsed?: boolean; // Whether subcharts are collapsed by default (default: false = expanded)
  chartType?: "line" | "bar"; // Override subchart type (default: bar for discrete, line for continuous/ratio)
}

export interface PlotConfig {
  id: string;
  measureId: MeasureId;
  label: string;
  chart: PlotChartType;
  subcharts?: SubchartsConfig;
  granularity?: TimeGrain;
  ratioComponents?: RatioComponents;
  ewma?: EwmaConfig;
  boxplot?: BoxplotConfig;
  stackedBar?: StackedBarConfig;
  distributionXmr?: DistributionXmrConfig;
}

interface PlotDefinition {
  measureId: MeasureId;
  chart: PlotChartType;
  id?: string;
  label?: string;
  subcharts?: SubchartToken | SubchartToken[] | SubchartsConfig;
  granularity?: TimeGrain;
  ratioComponents?: RatioComponents;
  ewma?: EwmaConfig;
  boxplot?: BoxplotConfig;
  stackedBar?: StackedBarConfig;
  distributionXmr?: DistributionXmrConfig;
}

const PLOT_DEFINITIONS: PlotDefinition[] = [
  // ===== Controllable Inputs =====

  // Campaign Structure

  // Filtering & Targeting

  // ML Model Performance
  {
    measureId: "ctr",
    chart: "xmr",
    subcharts: { dims: ["1D"], sortByDesc: "revenue_eur", collapsed: true },
    ratioComponents: { numerator: "clicks", denominator: "impressions" },
  },
  { measureId: "impression_ctr_model_logloss", chart: "xmr" },
  { measureId: "ctr_calibration_log_ratio", chart: "xmr" },
  { measureId: "impression_reengagement_model_logloss_1d", chart: "xmr" },
  { measureId: "ap_impression_reengagement_model_logloss_1d", chart: "xmr" },
  { measureId: "impression_reengagement_model_logloss_7d", chart: "xmr" },
  { measureId: "ap_impression_reengagement_model_logloss_7d", chart: "xmr" },
  { measureId: "reengagement_calibration_log_ratio_1d", chart: "xmr" },
  { measureId: "ap_reengagement_calibration_log_ratio_1d", chart: "xmr" },
  { measureId: "reengagement_calibration_log_ratio_7d", chart: "xmr" },
  { measureId: "ap_reengagement_calibration_log_ratio_7d", chart: "xmr" },
  { measureId: "win_rate_mse", chart: "xmr" },
  { measureId: "reason_unused_bid_too_low_share", chart: "xmr" },
  { measureId: "reason_unused_bid_too_high_share", chart: "xmr" },
  { measureId: "total_bid_multiplier", chart: "x" },

  // Bidding Logic
  { measureId: "repeated_win_loss_correction_coefficient", chart: "x" },
  {
    measureId: "impression_value",
    chart: "x",
    subcharts: { dims: ["1D"], sortByDesc: "revenue_eur", collapsed: true },
  },
  { measureId: "impression_value_shading_factor", chart: "x" },
  {
    measureId: "spend_adjustment",
    chart: "x",
    subcharts: { dims: ["country", "organization"], sortByDesc: "revenue_eur", collapsed: true },
  },
  {
    measureId: "spend_adjustment",
    chart: "boxplot",
    id: "spend_adjustment-by-audience-boxplot",
    label: "Spend Adjustment Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
    },
  },
  {
    measureId: "auto_spend_adjustment",
    chart: "x",
    subcharts: { dims: ["country", "organization"], sortByDesc: "revenue_eur", collapsed: true },
  },
  {
    measureId: "pacing_adjustment",
    chart: "x",
    subcharts: { dims: ["country", "organization"], sortByDesc: "revenue_eur", collapsed: true },
  },
  { measureId: "max_bid", chart: "x" },
  {
    measureId: "max_bid_shading_factor",
    chart: "x",
    subcharts: { dims: ["country", "organization"], sortByDesc: "revenue_eur", collapsed: true },
  },
  { measureId: "avg_bid_price", chart: "x", subcharts: { dims: ["1D"], sortByDesc: "revenue_eur", collapsed: true } },

  // ===== Less-Controllable Inputs =====

  // ===== Operational Outputs =====

  // Volume Metrics
  // { measureId: "bid_opportunities", chart: "line", subcharts: ["partner"], granularity: "week" },
  { measureId: "bids", chart: "ewma", subcharts: { dims: ["1D"], collapsed: true } },
  // { measureId: "bid_rate", chart: "line", subcharts: ["partner"] },
  { measureId: "impressions", chart: "ewma", subcharts: { dims: ["placement_type", "partner"], collapsed: true } },
  { measureId: "clicks", chart: "ewma", subcharts: { dims: ["1D"], collapsed: true } },

  // Operational Metrics
  {
    measureId: "win_rate",
    chart: "xmr",
    subcharts: { dims: ["organization", "placement_type", "partner"], sortByDesc: "revenue_eur", collapsed: true },
  },
  {
    measureId: "impression_rate",
    chart: "xmr",
    subcharts: { dims: ["organization", "placement_type", "partner"], sortByDesc: "revenue_eur", collapsed: true },
  },

  // Shares & Spread

  // Budget & Pacing Health
  {
    measureId: "selectivity",
    chart: "xmr",
    subcharts: {
      dims: ["organization", "campaign_type", "primary_prediction_flight_id"],
      collapsed: true,
    },
  },
  {
    measureId: "spend_adjustment_variance_p50",
    chart: "xmr",
    subcharts: {
      dims: ["organization", "campaign_type", "primary_prediction_flight_id"],
      collapsed: true,
    },
  },
  {
    measureId: "spend_adjustment_variance_p99",
    chart: "xmr",
    subcharts: {
      dims: ["organization", "campaign_type", "primary_prediction_flight_id"],
      collapsed: true,
    },
  },
  {
    measureId: "spend_adjustment_variance_minute_avg",
    chart: "xmr",
    subcharts: {
      dims: ["organization", "campaign_type", "primary_prediction_flight_id"],
      collapsed: true,
    },
  },
  {
    measureId: "spend_adjustment_churn_minute_avg",
    chart: "xmr",
    subcharts: {
      dims: ["organization", "campaign_type", "primary_prediction_flight_id"],
      collapsed: true,
    },
  },
  {
    measureId: "spend_adjustment_flip_rate_minute_avg",
    chart: "xmr",
    subcharts: {
      dims: ["organization", "campaign_type", "primary_prediction_flight_id"],
      collapsed: true,
    },
  },
  {
    measureId: "pacing_error_deviation_across_minutes",
    chart: "xmr",
    subcharts: {
      dims: ["organization", "campaign_type"],
      collapsed: true,
    },
  },

  // ===== Performance =====

  // { measureId: "total_budget_eur", chart: "xmr", subcharts: ["organization", "audience", "campaign_type"] },
  // { measureId: "budget_utilization", chart: "xmr-log", subcharts: ["organization", "audience", "campaign_type"] },
  { measureId: "margin", chart: "xmr" },
  { measureId: "impression_value_over_supply_cost", chart: "xmr" },
  { measureId: "ap_event_value_d1", chart: "xmr" },
  { measureId: "ap_event_value_d30", chart: "xmr" },
  { measureId: "ap_event_value_d30_over_supply_cost", chart: "xmr" },
  { measureId: "supply_cpm_eur", chart: "xmr" },
  { measureId: "supply_cost_eur", chart: "xmr", granularity: "week" },
  { measureId: "revenue_eur", chart: "xmr" },
  { measureId: "profit_eur", chart: "xmr" },
  {
    measureId: "profit_eur",
    chart: "stacked-bar",
    id: "profit_eur-by-organization-stacked",
    stackedBar: {
      dimension: "organization",
      maxCategories: 20,
    },
  },

  // Conversions (Self-Attributed)
  { measureId: "conversions_1d", chart: "xmr" },
  { measureId: "conversions_7d", chart: "xmr" },
  { measureId: "conversions_30d", chart: "xmr" },

  // Conversions (AP)
  { measureId: "ap_conversions_1d", chart: "xmr" },
  { measureId: "ap_conversions_7d", chart: "xmr" },
  { measureId: "ap_conversions_30d", chart: "xmr" },

  // MMP attribution ratio
  { measureId: "mmp_attribution_ratio_1d", chart: "xmr" },
  { measureId: "mmp_attribution_ratio_7d", chart: "xmr" },
  { measureId: "mmp_attribution_ratio_30d", chart: "xmr" },

  // Supply eCPX
  { measureId: "supply_ecpx_1d", chart: "xmr" },
  { measureId: "supply_ecpx_7d", chart: "xmr" },
  { measureId: "supply_ecpx_30d", chart: "xmr" },

  // Supply AP eCPX
  { measureId: "supply_ap_ecpx_1d", chart: "xmr" },
  { measureId: "supply_ap_ecpx_7d", chart: "xmr" },
  { measureId: "supply_ap_ecpx_30d", chart: "xmr" },

  // Customer Revenue
  { measureId: "customer_revenue_1d", chart: "xmr" },
  { measureId: "ap_customer_revenue_1d", chart: "xmr" },
  { measureId: "customer_revenue_7d", chart: "xmr" },
  { measureId: "ap_customer_revenue_7d", chart: "xmr" },
  { measureId: "customer_revenue_30d", chart: "xmr" },
  { measureId: "ap_customer_revenue_30d", chart: "xmr" },

  // eRPM
  { measureId: "eRPM_1d", chart: "xmr" },
  { measureId: "eRPM_7d", chart: "xmr" },
  { measureId: "eRPM_30d", chart: "xmr" },

  { measureId: "reengagements_1d", chart: "xmr" },
  { measureId: "ap_reengagements_1d", chart: "xmr" },
  { measureId: "reengagements_7d", chart: "xmr" },
  { measureId: "ap_reengagements_7d", chart: "xmr" },
  {
    measureId: "reengagement_rate_1d",
    chart: "xmr",
    subcharts: { dims: ["1D"], sortByDesc: "revenue_eur", collapsed: true },
    ratioComponents: { numerator: "reengagements_1d", denominator: "impressions" },
  },
  {
    measureId: "ap_reengagement_rate_1d",
    chart: "xmr",
    subcharts: { dims: ["1D"], sortByDesc: "revenue_eur", collapsed: true },
    ratioComponents: { numerator: "ap_reengagements_1d", denominator: "impressions" },
  },
  {
    measureId: "reengagement_rate_7d",
    chart: "xmr",
    subcharts: { dims: ["1D"], sortByDesc: "revenue_eur", collapsed: true },
    ratioComponents: { numerator: "reengagements_7d", denominator: "impressions" },
  },
  {
    measureId: "ap_reengagement_rate_7d",
    chart: "xmr",
    subcharts: { dims: ["1D"], sortByDesc: "revenue_eur", collapsed: true },
    ratioComponents: { numerator: "ap_reengagements_7d", denominator: "impressions" },
  },

  // ROAS
  { measureId: "roas_1d", chart: "xmr" },
  { measureId: "ap_roas_1d", chart: "xmr" },
  { measureId: "supply_roas_1d", chart: "xmr" },
  { measureId: "supply_ap_roas_1d", chart: "xmr" },
  { measureId: "roas_7d", chart: "xmr" },
  { measureId: "ap_roas_7d", chart: "xmr" },
  { measureId: "supply_roas_7d", chart: "xmr" },
  { measureId: "supply_ap_roas_7d", chart: "xmr" },
  { measureId: "roas_30d", chart: "xmr" },
  { measureId: "ap_roas_30d", chart: "xmr" },
  { measureId: "supply_roas_30d", chart: "xmr" },
  { measureId: "supply_ap_roas_30d", chart: "xmr" },

  // Other

  // ===== User Targeting =====

  // ===== Distribution Analysis =====
  {
    measureId: "impressions",
    chart: "boxplot",
    id: "impressions-by-audience-boxplot",
    label: "Impressions Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
      logScale: true,
    },
  },
  {
    measureId: "clicks",
    chart: "boxplot",
    id: "clicks-by-audience-boxplot",
    label: "Clicks Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
      logScale: true,
    },
  },
  {
    measureId: "ap_conversions_1d",
    chart: "boxplot",
    id: "ap-conversions-1d-by-audience-boxplot",
    label: "AP Conversions (1d) Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
      logScale: true,
    },
  },
  {
    measureId: "ap_conversions_7d",
    chart: "boxplot",
    id: "ap-conversions-7d-by-audience-boxplot",
    label: "AP Conversions (7d) Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
      logScale: true,
    },
  },
  {
    measureId: "ap_customer_revenue_1d",
    chart: "boxplot",
    id: "ap_customer_revenue_1d-by-audience-boxplot",
    label: "AP Customer Revenue (1d) Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
      logScale: true,
    },
  },
  {
    measureId: "ap_customer_revenue_1d",
    chart: "distribution-xmr",
    id: "ap-customer-revenue-1d-distribution-xmr",
    distributionXmr: {
      dimension: "audience",
      distribution: "log-normal",
    },
  },
  {
    measureId: "ap_customer_revenue_7d",
    chart: "boxplot",
    id: "ap_customer_revenue_7d-by-audience-boxplot",
    label: "AP Customer Revenue (7d) Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
      logScale: true,
    },
  },
  {
    measureId: "ap_roas_1d",
    chart: "boxplot",
    id: "ap_roas_1d-by-audience-boxplot",
    label: "AP ROAS (1d) Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.15,
      upperExtreme: 0.85,
    },
  },
  {
    measureId: "supply_ap_ecpx_1d",
    chart: "boxplot",
    id: "supply_ap_ecpx_1d-by-audience-boxplot",
    label: "Supply AP eCPX (1d) Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
      logScale: true,
    },
  },
  {
    measureId: "supply_ecpc",
    chart: "boxplot",
    id: "supply_ecpc-by-audience-boxplot",
    label: "Supply eCPC Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
    },
  },
  {
    measureId: "profit_eur",
    chart: "boxplot",
    id: "profit_eur-by-audience-boxplot",
    label: "Profit Eur Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
    },
  },
  {
    measureId: "avg_bid_price",
    chart: "boxplot",
    id: "avg_bid_price-by-audience-boxplot",
    label: "Avg. Bid Price CPM (EUR) Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
    },
  },
  {
    measureId: "campaign_cpc",
    chart: "boxplot",
    id: "campaign_cpc-by-audience-boxplot",
    label: "Campaign CPC (EUR) Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
    },
  },
  {
    measureId: "campaign_cpc",
    chart: "distribution-xmr",
    id: "campaign_cpc-distribution-xmr",
    distributionXmr: {
      dimension: "audience",
      distribution: "log-normal",
    },
  },
  {
    measureId: "impression_ctr_model_logloss",
    chart: "boxplot",
    id: "ctr-logloss-by-audience-boxplot",
    label: "CTR log-loss by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
    },
  },
  {
    measureId: "win_rate_mse",
    chart: "boxplot",
    id: "win-rate-mse-by-audience-boxplot",
    label: "Win Rate MSE Distribution by Audience",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
    },
  },

  // ===== Unique Users =====

  // Rate metrics for unique users funnel
  {
    measureId: "unique_users_impression_rate",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },
  {
    measureId: "unique_users_click_rate",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },

  // These metrics are forced to daily granularity because unique counts cannot be meaningfully aggregated
  {
    measureId: "unique_users_bid",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },
  {
    measureId: "unique_users_impression",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },
  {
    measureId: "unique_users_click",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },
  {
    measureId: "unique_users_reengagement",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },
  {
    measureId: "unique_users_conversions",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },
  {
    measureId: "unique_users_revenue",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },
  {
    measureId: "unique_users_partner_reengagement",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },
  {
    measureId: "unique_users_ap_conversions",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },
  {
    measureId: "unique_users_ap_revenue",
    chart: "xmr",
    granularity: "day",
    subcharts: { dims: ["ad_type", "placement_type"], collapsed: true, chartType: "line" },
  },

  // Unique users boxplots by audience and country
  {
    measureId: "unique_users_bid",
    chart: "boxplot",
    id: "unique-users-bid-by-audience-boxplot",
    label: "Unique Users (Bid) Distribution by Audience",
    granularity: "day",
    boxplot: {
      aggregateDimension: "audience",
      lowerExtreme: 0.05,
      upperExtreme: 0.95,
      logScale: true,
    },
  },
  {
    measureId: "unique_users_impression_rate",
    chart: "boxplot",
    id: "unique-users-impression-rate-by-audience-boxplot",
    label: "Unique Users Impression Rate Distribution by Audience",
    granularity: "day",
    boxplot: { aggregateDimension: "audience", lowerExtreme: 0.05, upperExtreme: 0.95 },
  },
  {
    measureId: "unique_users_click_rate",
    chart: "boxplot",
    id: "unique-users-click-rate-by-audience-boxplot",
    label: "Unique Users Click Rate Distribution by Audience",
    granularity: "day",
    boxplot: { aggregateDimension: "audience", lowerExtreme: 0.05, upperExtreme: 0.95 },
  },
];

const measureById = new Map<MeasureId, MeasureDef>(MEASURES.map(measure => [measure.id, measure]));

export const PLOTS: PlotConfig[] = PLOT_DEFINITIONS.map((definition, index) => {
  const measure = measureById.get(definition.measureId);
  if (!measure) {
    throw new Error(`Plot config references unknown measure ${definition.measureId}`);
  }

  return {
    id: definition.id ?? `${definition.measureId}-${index}`,
    measureId: measure.id,
    label: definition.label ?? measure.label,
    chart: definition.chart,
    subcharts: normalizeSubcharts(definition.subcharts),
    granularity: definition.granularity,
    ratioComponents: definition.ratioComponents,
    ewma: definition.ewma,
    boxplot: definition.boxplot,
    stackedBar: definition.stackedBar,
    distributionXmr: definition.distributionXmr,
  } satisfies PlotConfig;
});

export const FORCED_MEASURE_GRANULARITY: Partial<Record<MeasureId, TimeGrain>> = Object.fromEntries(
  PLOTS.filter(plot => plot.granularity != null).map(plot => [plot.measureId, plot.granularity as TimeGrain]),
);

function normalizeSubcharts(spec?: PlotDefinition["subcharts"]): SubchartsConfig | undefined {
  if (!spec) return undefined;

  // Inline narrowing: object with a dims array means it's already SubchartsConfig
  if (typeof spec === "object" && !Array.isArray(spec) && "dims" in spec) {
    const cfg = spec as SubchartsConfig;
    return { ...cfg, limit: cfg.limit ?? 10 };
  }

  // At this point, spec is SubchartToken | SubchartToken[]
  const tokens: SubchartToken[] = [];

  const addToken = (token: SubchartToken) => {
    if (Array.isArray(token)) {
      tokens.push([token[0], token[1]]);
    } else {
      tokens.push(token);
    }
  };

  if (Array.isArray(spec)) {
    for (const entry of spec) {
      addToken(entry);
    }
  } else {
    addToken(spec);
  }

  return tokens.length > 0 ? { dims: tokens, limit: 10 } : undefined;
}
